import { cn } from '@ifixit/ui/lib/utils';
import type { ReactNode } from 'react';

/**
 * The muted one-liner under a control — what the field means, when it takes
 * effect, how to commit it. Shared so every editor field reads at one size
 * rather than each caller picking its own.
 */
export const FIELD_HINT_CLASS = 'text-xs text-gray-500';

/**
 * The quieter label used over a column of inputs — Wiki Info's and Suppliers'
 * grid headers, and the rename fields that sit in the same kind of row. Says
 * which column you're in without competing with the field label above it.
 */
export const COLUMN_LABEL_CLASS = 'text-xs font-medium text-gray-500';

/**
 * A section heading inside a card — Flags, Wiki Info, Attached Documents.
 * One weight step above FIELD_LABEL_CLASS so a section and the fields under
 * it don't render identically now that each tab is a single card and the
 * card border no longer separates them.
 */
export const SECTION_HEADING_CLASS = 'text-sm font-semibold text-gray-950';

/**
 * The label above one control. Exported for the fields that have no control
 * to label — a read-only value still has to line up with the editable ones.
 */
export const FIELD_LABEL_CLASS = 'text-sm font-medium text-gray-950';

/**
 * A labeled form field for the v2 editors: small-label style (the section
 * heading stays the enclosing card's h2), with an optional hint below.
 * Wrapping in a <label> means clicking the label text focuses the control.
 */
export function Field({
   label,
   hint,
   children,
}: {
   label: string;
   hint?: string;
   children: ReactNode;
}) {
   return (
      <label className="block">
         <span className={cn('mb-1 block', FIELD_LABEL_CLASS)}>{label}</span>
         {children}
         {hint && <span className={cn('mt-1 block', FIELD_HINT_CLASS)}>{hint}</span>}
      </label>
   );
}
